






























































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { DateTime } from "luxon";
import {
  DryingProjectOverview,
  DamagePatternConclusionState,
  getProjectOverviewEndDate,
} from "@/data/models/DryingProjectOverview";
import {
  DeviceCountDisplay,
  getDeviceCountInfo,
} from "@/utils/ProjectDeviceCount";

import {
  ApprovalResult,
  containsOnlyLeakDetectionInfo,
  doesProjectTypeIdHaveDrying,
  doesProjectTypeIdHaveRestoration,
  getProjectTypeCode,
  getProjectTypeIdOfProject,
  isFlexProject,
  isPortalOnlyProject,
  ProjectConclusionState,
  ProjectTypeConfig,
} from "@/data/models/ProjectDetails";

import LeakDetectionProjectOverview from "@/data/models/LeakDetectionProjectOverview";
import ProjectTypeDisplayComponent from "@/components/project/type/ProjectTypeDisplay.vue";

import {
  getProjectConclusionState,
  ContactPersonOverview,
  isProjectDeleted,
} from "@/data/models/ProjectOverview";

import { formatDateShort, formatDurationLong, timestampToDate } from "@/utils/DateFormatter";
import { resolveProjectRowClick } from "@/utils/RestoreAndDeleteProjectAction";
import { isProjectNumberSameAsName } from "@/utils/ProjectNameCalculation";
import { CardState, getCardColor, getPortalOnlyProjectCardStates } from "@/utils/ProjectOverviewUtils";
import { formatLocationInfo, formatNameInfo } from "@/utils/LocalisationHelper";

import { ButtonState } from "@/components/common/StateButton.vue";

import ProjectOverviewStatusComponent from "@/components/project/list/ProjectOverviewStatus.vue";

import * as _ from "lodash";

import router from "@/router/router";
import { findUser } from "@/data/repository/UserRepository";
import { Branch, getBranchName } from "@/data/models/Branch";
import { getIresClientType, IresClientType } from "@/data/models/IresClient";

import {
  LatestIresDeviceInfo,
  isCurrentAssignmentMissingProgramChain,
} from "@/data/models/LatestIresDeviceInfo";
import { observeLatestIresDeviceInfo } from "@/data/repository/IresDeviceRepository";
import { isSimulatingErp, getClientId } from "@/data/repository/ClientRepository";
import { UserModule } from "@/store/modules/UserModule";
import {
  areOnSameDay,
} from "@/utils/DateHelper";
import { firestore } from "firebase";
import ProjectListTypeDisplayComponent from "../type/ProjectListDisplayType.vue";
import { ClientModule } from "@/store/modules/ClientModule";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { COLOR_PROJECT_TILE_LOCKED, COLOR_PROJECT_TILE_ON_HOLD } from "@/utils/colors";

interface AssignedDevices {
  [key: string]: LatestIresDeviceInfo;
}

interface AssignedDevicesInfo {
  assignedDevicesSerialNumbers: string[];
  assignedDevices: AssignedDevices;
}

@Component({
  components: {
    "project-list-type-display": ProjectListTypeDisplayComponent,
    "project-type-display": ProjectTypeDisplayComponent,
    "project-overview-status": ProjectOverviewStatusComponent,
  },
})
export default class ProjectOverviewComponent extends Vue {
  @Prop() readonly projectOverview!:
    | DryingProjectOverview
    | LeakDetectionProjectOverview;

  @Prop() showProjectStatus!: boolean;
  @Prop() windowWidth!: number;
  @Prop() branchesOfClient!: Branch[];
  @Prop() projectTypes!: ProjectTypeConfig[];
  @Prop() isArchivedProject!: boolean;
  @Prop({ default: "" }) backgroundStyle!: string;
  @Prop({ default: false }) hasReachedMaximumSelectionLimit!: boolean;
  @Prop({ default: false }) isSelectionMode!: boolean;
  @Prop({ default: false }) isSelected!: boolean;
  @Prop({ default: false }) isSearchMode!: boolean;

  @Watch("isSelected")
  onIsSelectedChanged() {
    this.isProjectSelected = this.isSelected;
  }

  assignedDevicesDetails = this.calculateInitalAssignedDevicesInfo();
  isProjectSelected = false;

  calculateInitalAssignedDevicesInfo(): AssignedDevicesInfo {
    const result = {
      assignedDevicesSerialNumbers: [],
      assignedDevices: {},
    };
    const dryingProjectOverview = this.projectOverview as DryingProjectOverview;

    if (!dryingProjectOverview) {
      return result;
    }

    if (!dryingProjectOverview.assignedPanels) {
      return result;
    }

    return {
      assignedDevicesSerialNumbers: dryingProjectOverview.assignedPanels,
      assignedDevices: {},
    };
  }

  get isDeletedProject() {
    return isProjectDeleted(this.projectOverview);
  }

  get projectEndStateString() {
    const projectConclusionState = getProjectConclusionState(this.projectOverview);

    switch (projectConclusionState) {
      case ProjectConclusionState.ARCHIVED:
        return this.$t("common.archived");
      case ProjectConclusionState.DELETED:
        return this.$t("common.deleted");
      case ProjectConclusionState.MOVED_TO_FINSIHED:
        return this.$t("common.movedToFinished");
      case ProjectConclusionState.RESTORED:
        return this.$t("common.restored");
      default:
        return this.$t("common.concluded");
    }
  }

  /**
   * GZ tile previews were released on 14.05.2026. Projects started before
   * that date do not have the required step-complete flags, so we hide tiles
   * for them (restoring the pre-release behaviour).
   */
  private static readonly GZ_TILES_CUTOFF = new Date(2026, 4, 14); // 14 May 2026

  get showProjectStatusCards() {
    if (isPortalOnlyProject(this.projectOverview)) {
      const displaySettings = ProjectManagementSettingsModule.projectManagementSettings.displaySettings;
      if (!displaySettings?.projectOverviewTilesForOfficeProjects) {
        return false;
      }
      const start = this.projectOverview.startDate?.toDate();
      if (!start || start < ProjectOverviewComponent.GZ_TILES_CUTOFF) {
        return false;
      }
    }
    return true;
  }

  projectButtonState = ButtonState.LINK;

  /**
   * Right-aligns the step tiles only on wide desktops; below 960 px it pushed them into the right
   * half of the row, under nothing, so they now start on the row's left like at 960-1350 px.
   */
  get showCardSpacer() {
    return this.windowWidth > 1350;
  }

  get secondColumnCols() {
    return this.showProjectStatus ? 2 : 3;
  }

  get contactPersons() {
    const result = this.projectOverview.contactPersons ?? [];

    return result;
  }

  /**
   * Phones and iPads (below 1264 px): the list row instead of the desktop columns, whose 5/12
   * name column wrapped long addresses on an iPad mini (Taimoor, 2026-10-06). From 960 px the
   * desktop columns put the tiles on a line of their own under the row (iPad Pro 13 in portrait),
   * because the status column sits beside them only from 1264 px.
   */
  get isCompactRow() {
    return this.$vuetify.breakpoint.mdAndDown;
  }

  /** Phones (below 600 px): the devices button sits under date and owner instead of in the status column. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The contact persons on one line, for the phone row's supporting text. */
  get contactPersonNames() {
    return this.contactPersons
      .map((contactPerson) => this.getContactPersonName(contactPerson))
      .filter((name) => !!name)
      .join(", ");
  }

  getContactPersonName(contactPerson: ContactPersonOverview) {
    return formatNameInfo(contactPerson);
  }

  get projectDevices() {
    const dryingProjectOverview = this.projectOverview as DryingProjectOverview;
    const projectDevicesText = [];

    if (dryingProjectOverview.numContactPersons > 1) {
      projectDevicesText.push(this.$tc("projectList.numApartments", dryingProjectOverview.numContactPersons));
    }

    if (dryingProjectOverview.numRooms > 1 && dryingProjectOverview.numRooms !== dryingProjectOverview.numContactPersons) {
      projectDevicesText.push(this.$tc("projectList.numRooms", dryingProjectOverview.numRooms));
    }

    const deviceText = this.getDeviceText(dryingProjectOverview);

    if (deviceText) {
      projectDevicesText.push(deviceText);
    }

    return projectDevicesText.join(", ");
  }

  /**
   * Renders the device text as "1 von 3 Geräten aktiv" / "3 Geräte aktiv" /
   * "3 Geräte", depending on how many of the devices the project has used are
   * still on site. The rule itself lives in `getDeviceCountInfo`.
   */
  private getDeviceText(dryingProjectOverview: DryingProjectOverview): string | null {
    const { display, activeDevices, totalDevices } = getDeviceCountInfo(dryingProjectOverview);

    switch (display) {
      case DeviceCountDisplay.NONE_ACTIVE:
        return this.$tc("projectList.numAllDevicesNoneActive", totalDevices);
      case DeviceCountDisplay.ALL_ACTIVE:
        return this.$tc("projectList.numAllDevicesAllActive", totalDevices);
      case DeviceCountDisplay.SOME_ACTIVE:
        return this.$t("projectList.numAllDevices", [activeDevices, totalDevices]).toString();
      default:
        return null;
    }
  }

  get projcetBranch() {
    if (this.projectOverview.branch) {
      const branch = this.branchesOfClient.find(
        (branch) => branch.id === this.projectOverview.branch
      );

      return branch ? getBranchName(branch) : null;
    } else {
      return null;
    }
  }

  get projectTitle(): string {
    return isSimulatingErp() ? this.projectOverview.projectNumberIres ?? this.projectOverview.projectName : this.projectOverview.projectName;
  }

  get projectNumber(): string {
    const { projectNameErp, projectNumberIres, projectNumber: projectNumberOffice } = this.projectOverview;
    const hasOfficeNumber = !!(projectNumberOffice && projectNumberOffice !== "");

    // Show office number if available, otherwise IRES number
    let projectNumber = hasOfficeNumber
      ? projectNumberOffice
      : projectNumberIres ?? "";

    // Append ERP name if present
    if (projectNameErp) {
      if (
        projectNumber &&
        projectNumber !== "" &&
        !isProjectNumberSameAsName(projectNumber, projectNameErp)
      ) {
        projectNumber = `${projectNumber} / ${projectNameErp}`;
      } else {
        projectNumber = projectNameErp;
      }
    }

    // If project number matches the title exactly, show nothing
    if (this.projectTitle === projectNumber) {
      return "";
    }

    return projectNumber;
  }

  get location(): string {
    if (!this.projectOverview.location) {
      return "";
    }

    return formatLocationInfo(this.projectOverview.location);
  }

  get projectEndDate() {
    return getProjectOverviewEndDate(this.projectOverview);
  }

  get projectConclusionState() {
    return this.projectOverview.conclusion?.conclusionState ??
      this.projectOverview.projectState;
  }

  get isRestoredProject() {
    return this.projectConclusionState === ProjectConclusionState.RESTORED;
  }

  get isSanTecClient() {
    return getIresClientType(getClientId()) === IresClientType.SanTec;
  }

  get timeRange() {
    const isSanTec = this.isSanTecClient;
    const projectStartDateForSanTec = firestore.Timestamp.fromDate(new Date(((this.projectOverview as DryingProjectOverview).dryingProjectSteps?.projectAccepted?.creationTimestamp)));
    const isProjectStartDateForSanTecValid = 
      projectStartDateForSanTec && 
      projectStartDateForSanTec.toMillis() < this.projectOverview.startDate?.toMillis();

    const projectStartDate = isSanTec && isProjectStartDateForSanTecValid
      ? projectStartDateForSanTec
      : this.projectOverview.startDate ?? this.projectOverview.projectStartedBy?.recordedAt;
    
    if (this.projectEndDate) {
      const startYear = timestampToDate(projectStartDate)?.getFullYear();
      const endYear = timestampToDate(this.projectEndDate)?.getFullYear();

      if (
        this.isDeletedProject
      ) {
        const projectDeletedAt = this.projectOverview.conclusion
          ? this.projectOverview.conclusion.conclusionDate
          : this.projectOverview.lastOperationAt ?? this.projectEndDate;

        return this.$t("common.onDate", [
          formatDateShort(projectDeletedAt),
        ]).toString();
      }
      else if (
        areOnSameDay(projectStartDate, this.projectEndDate) || 
        !startYear
      ) {
        return this.$t("common.onDate", [
          formatDateShort(this.projectEndDate),
        ]).toString();
      }

      return this.$t("common.fromUntil", [
        formatDateShort(projectStartDate, undefined, undefined, startYear !== endYear),
        formatDateShort(this.projectEndDate, undefined, undefined, startYear !== endYear),
      ]).toString();
    } else {
      const timeDiff =
        Math.ceil(
          -DateTime.fromJSDate(
            this.projectOverview.startDate?.toDate()
          ).diff(DateTime.fromJSDate(new Date()).endOf("day"), ["days"]).days
        ) *
        60 *
        24;

      return (
        this.$t("common.since", [
          formatDateShort(projectStartDate),
        ]).toString() +
        (timeDiff > 0 ? ` (${formatDurationLong(this, timeDiff, false)})` : "")
      );
    }
  }

  get projectFinished() {
    if (this.projectEndDate) {
      return this.$t("projectList.projectFinished");
    }

    return null;
  }

  get showDevices() {
    const dryingProjectOverview = this.projectOverview as DryingProjectOverview;

    if (!dryingProjectOverview) {
      return false;
    }

    if (!dryingProjectOverview.assignedPanels) {
      return false;
    }

    return dryingProjectOverview.assignedPanels.length !== 0;
  }

  get devices() {
    const dryingProjectOverview = this.projectOverview as DryingProjectOverview;

    if (!dryingProjectOverview) {
      return "";
    }

    if (!dryingProjectOverview.assignedPanels) {
      return "";
    }

    const numPanels = _.uniq(dryingProjectOverview.assignedPanels).length;

    if (numPanels === 0) {
      return "";
    }

    return this.$t("common.numPanels", [numPanels]);
  }

  get isAdministratorClientUser() {
    return ClientModule.isAdministratorClientUser;
  }

  /**
   * A row of the archive asks the list to offer restore / delete rather than
   * opening the project. The list owns that dialog, so that one instance
   * serves the whole page instead of one per rendered row.
   */
  openDetails() {
    const outcome = resolveProjectRowClick({
      isSelectionMode: this.isSelectionMode,
      isArchivedProject: this.isArchivedProject,
      isAdministratorClientUser: this.isAdministratorClientUser,
      hasReachedMaximumSelectionLimit: this.hasReachedMaximumSelectionLimit,
    });

    this.$emit(outcome.event);

    if (outcome.clearsOwnSelection) {
      this.isProjectSelected = false;
    }
  }

  get projectTypeId() {
    return this.projectOverview.projectTypeId ?? getProjectTypeIdOfProject(this.projectOverview);
  }

  get hideBadge() {
    return getProjectTypeCode(this, this.projectTypeId, this.projectTypes) === '--';
  }

  get cardStates(): Array<CardState> {
    const projectTypeId = this.projectTypeId;

    const hasDrying = doesProjectTypeIdHaveDrying(projectTypeId);
    const hasRestoration = doesProjectTypeIdHaveRestoration(projectTypeId);

    if (
      this.projectOverview.projectApproval?.approvalResult ===
      ApprovalResult.PENDING
    ) {
      const numCards = hasDrying || hasRestoration ? 8 : 4;

      const result: CardState[] = [];

      for (let index = 0; index < 8; index++) {
        result.push({
          shouldBeDisplayed: index < numCards,
          name: (index + 1).toString(),
          color: index === 0 ? COLOR_PROJECT_TILE_ON_HOLD : COLOR_PROJECT_TILE_LOCKED,
        });
      }

      return result;
    }

    if (isPortalOnlyProject(this.projectOverview)) {
      const start = this.projectOverview.startDate?.toDate();
      if (start && start >= ProjectOverviewComponent.GZ_TILES_CUTOFF) {
        const dryingProjectOverview = this
          .projectOverview as DryingProjectOverview;

        if (dryingProjectOverview) {
          const isApproved = dryingProjectOverview.damagePatternConclusionComplete === true;

          return getPortalOnlyProjectCardStates(
            isApproved,
            dryingProjectOverview.dryingSetupComplete,
            dryingProjectOverview.dryingProgressComplete,
            dryingProjectOverview.devicePackupComplete,
            dryingProjectOverview.projectConclusionComplete,
          );
        }
      }
      // Old GZ projects (before 14.05.2026): fall through to flex/standard logic
    }

    if (isFlexProject(this.projectOverview)) {
      const dryingProjectOverview = this
        .projectOverview as DryingProjectOverview;

      if (dryingProjectOverview) {
        const wasRejected =
          dryingProjectOverview.projectApproval?.approvalResult ===
          ApprovalResult.REJECTED;

        const noDryingNecessary =
          dryingProjectOverview.damagePatternConclusionState ===
            DamagePatternConclusionState.NO_DRYING_NECESSARY ||
          dryingProjectOverview.damagePatternConclusionState ===
            DamagePatternConclusionState.NO_CONTRACT

        return [
          dryingProjectOverview.clientDataComplete ?? false,
          dryingProjectOverview.documentationAndLeakDetectionComplete,
          dryingProjectOverview.damagePatternComplete,
          dryingProjectOverview.damagePatternConclusionComplete,

          dryingProjectOverview.dryingSetupComplete,
          dryingProjectOverview.dryingProgressComplete,
          dryingProjectOverview.dryingSetupComplete === true
            ? dryingProjectOverview.devicePackupComplete
            : dryingProjectOverview.deviceSetupComplete,
          dryingProjectOverview.projectConclusionComplete,
        ].map((element, index) => {
          let color = wasRejected
            ? index === 0
              ? getCardColor(true)
              : COLOR_PROJECT_TILE_LOCKED
            : index < 4 ||
              (this.isProjectSetupComplete(dryingProjectOverview) &&
                dryingProjectOverview.damagePatternConclusionState ===
                  DamagePatternConclusionState.CARRY_OUT_DRYING)
              ? getCardColor(element ?? undefined)
              : COLOR_PROJECT_TILE_LOCKED;

          if (
            index === 3 &&
            dryingProjectOverview.damagePatternConclusionComplete &&
            dryingProjectOverview.damagePatternConclusionState ===
              DamagePatternConclusionState.ON_HOLD
          ) {
            color = COLOR_PROJECT_TILE_ON_HOLD;
          }

          if (!hasDrying) {
            if (index > 3 && index < 7) {
              color = COLOR_PROJECT_TILE_LOCKED;
            }
          }

          return {
            shouldBeDisplayed: wasRejected
              ? index === 0
              : hasDrying || hasRestoration
                ? index < 4 || !noDryingNecessary
                : index < 4,
            name: (index + 1).toString(),
            color: color,
          };
        });
      }
    } else if (containsOnlyLeakDetectionInfo(this.projectOverview)) {
      const leakDetectionProjectOverview = this
        .projectOverview as LeakDetectionProjectOverview;

      if (leakDetectionProjectOverview) {
        return [
          leakDetectionProjectOverview.clientDataComplete,
          leakDetectionProjectOverview.leakDetectionPreparationComplete,
          leakDetectionProjectOverview.leakDetectionExecuteComplete,
          leakDetectionProjectOverview.leakDetectionConclusionComplete,

          false,
          false,
          false,
          false,
        ].map((element, index) => {
          return {
            shouldBeDisplayed: index < 4,
            name: (index + 1).toString(),
            color: getCardColor(element ?? undefined),
          };
        });
      }
    } else {
      const dryingProjectOverview = this
        .projectOverview as DryingProjectOverview;

      if (dryingProjectOverview) {
        const wasRejected =
          dryingProjectOverview.projectApproval?.approvalResult ===
          ApprovalResult.REJECTED;

        const noDryingNecessary =
          dryingProjectOverview.damagePatternConclusionState ===
          DamagePatternConclusionState.NO_DRYING_NECESSARY;

        return [
          dryingProjectOverview.clientDataComplete,
          dryingProjectOverview.damagePatternComplete,
          dryingProjectOverview.damagePatternConclusionComplete,
          dryingProjectOverview.dryingSetupComplete,

          dryingProjectOverview.deviceSetupComplete,
          dryingProjectOverview.dryingProgressComplete,
          dryingProjectOverview.dryingSetupComplete === true
            ? dryingProjectOverview.devicePackupComplete
            : dryingProjectOverview.deviceSetupComplete,
          dryingProjectOverview.projectConclusionComplete,
        ].map((element, index) => {
          return {
            shouldBeDisplayed: wasRejected
              ? index === 0
              : index < 3 || !noDryingNecessary,
            name: (index + 1).toString(),
            color: wasRejected
              ? index === 0
                ? getCardColor(true)
                : COLOR_PROJECT_TILE_LOCKED
              : index < 3 ||
                (this.isProjectSetupComplete(dryingProjectOverview) &&
                  dryingProjectOverview.damagePatternConclusionState ===
                    DamagePatternConclusionState.CARRY_OUT_DRYING)
              ? getCardColor(element ?? undefined)
              : COLOR_PROJECT_TILE_LOCKED,
          };
        });
      }
    }
    return [];
  }

  isProjectSetupComplete(projectOverview: DryingProjectOverview): boolean {
    if (
      !projectOverview.clientDataComplete ||
      !projectOverview.damagePatternComplete ||
      !projectOverview.damagePatternConclusionComplete
    ) {
      return false;
    }

    return true;
  }

  get projectFinishedText(): string | null {
    return null;
  }

  get showProjectFinishedText(): boolean {
    return this.projectFinishedText !== null;
  }

  get wasFinalReportSend(): boolean {
    return this.projectOverview.wasFinalReportSend;
  }

  get projectOwner() {
    const owner = findUser(this.projectOverview.projectOwner)?.nickname ?? UserModule.userNicknameByStaffNumber(this.projectOverview.projectOwnerStaffNumber ?? "");
    const projectSupervisor =
      findUser(this.projectOverview.projectSupervisorId)?.nickname ?? UserModule.userNicknameByStaffNumber(this.projectOverview.projectSupervisorStaffNumber ?? "");

    if (!owner && !projectSupervisor) {
      return null;
    }

    if (owner !== projectSupervisor) {
      return `(${owner ?? "."}/${projectSupervisor ?? "."})`;
    }

    return owner;
  }

  openDevicesOfProject() {
    router.push({
      path: `/main/projects/${this.projectOverview.id}`,
      query: {
        openDevices: "1",
      },
    });
  }

  subscriptions: Array<() => void> = [];
  isObservingAssignedDeviceDetails = false;

  get showAssignmentWarning() {
    const assignedDevicesDetails = this.assignedDevicesDetails;
    const serialNumbers = assignedDevicesDetails.assignedDevicesSerialNumbers;
    const numAssignedDevices = serialNumbers.length;
    if (numAssignedDevices === 0) {
      return false;
    }

    const assignedDevices = assignedDevicesDetails.assignedDevices;
    const latestIresDeviceInfo = Object.values(assignedDevices);

    if (latestIresDeviceInfo.length !== numAssignedDevices) {
      return false;
    }

    return latestIresDeviceInfo.some((latestIresDeviceInfo) =>
      isCurrentAssignmentMissingProgramChain(latestIresDeviceInfo)
    );
  }

  get assignedDevicesButtonState() {
    if (this.showAssignmentWarning) {
      return ButtonState.LINK_ACTION;
    } else {
      return ButtonState.LINK;
    }
  }

  visibilityChanged(isVisible: boolean) {
    if (!isVisible) {
      return;
    }

    if (this.isObservingAssignedDeviceDetails) {
      return;
    }

    this.isObservingAssignedDeviceDetails = true;

    const serialNumbers = this.assignedDevicesDetails
      .assignedDevicesSerialNumbers;

    if (serialNumbers.length === 0) {
      return;
    }

    const observeLatestIresDeviceInfoSubscriptions = serialNumbers.flatMap(
      (serialNumber) =>
        observeLatestIresDeviceInfo(serialNumber, (iresDeviceInfo) => {
          this.assignedDevicesDetails = {
            assignedDevicesSerialNumbers: this.assignedDevicesDetails
              .assignedDevicesSerialNumbers,
            assignedDevices: {
              ...this.assignedDevicesDetails.assignedDevices,
              [iresDeviceInfo.iresDevice.serialNo]: iresDeviceInfo,
            },
          };
        })
    );

    this.subscriptions.push(...observeLatestIresDeviceInfoSubscriptions);
  }

  mounted() {
    this.isProjectSelected = this.isSelected;
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription();
    });
  }
}
